import {ElasticParser, ElasticSearchProperty} from '../factory.parsers';
import {buildQueryAstroCircle} from '../../query/elastic.queries';
import {NumberParser} from './number.parser';

const raDegExp = '(36[0]|(3[0-5][0-9]|[12][0-9][0-9]|[0-9]?[0-9])(\\.\\d+)?)';
const raHexExp = '(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](\\.\\d+)?';
const raSimbadExp = '(0[0-9]|1[0-9]|2[0-3])h[0-5][0-9]m[0-5][0-9](\\.\\d+)?s';
const raAllExp = `(${raDegExp}|${raHexExp}|${raSimbadExp})`;

const raGtExp = `>${raDegExp}`;
const raLtExp = `<${raDegExp}`;
const raRangeExp = `${raDegExp}\\.\\.${raDegExp}`;
const raAllIntervalExp = `(${raGtExp}|${raLtExp}|${raRangeExp})`;

const decDegExp = '(\\+|-)?(((0?[0-9]|[1-8][0-9])(\\.\\d+)?)|90)';
const decHexExp = '(\\+|-)?(0[0-9]|[1-8][0-9]):[0-5][0-9]:[0-5][0-9](\\.\\d+)?';
const decSimbadExp = '(\\+|-)?(0[0-9]|[1-8][0-9])d[0-5][0-9]m[0-5][0-9](\\.\\d+)?s';
const decAllExp = `(${decDegExp}|${decHexExp}|${decSimbadExp})`;

const decGtExp = `>${decDegExp}`;
const decLtExp = `<${decDegExp}`;
const decRangeExp = `${decDegExp}\\.\\.${decDegExp}`;
const decAllIntervalExp = `(${decGtExp}|${decLtExp}|${decRangeExp})`;

export const radiusExp = '(,\\s*\\d+(\\.\\d+)?)?';
export const raDecRadiusRegExp = new RegExp(`^${raAllExp}\\s+${decAllExp}\\s*${radiusExp}$`);
export const raDecIntervalRegExp = new RegExp(`^${raAllIntervalExp}\\s+${decAllIntervalExp}$`);

export const defaultRaDecRadius = 10;

export class RaDecParserParser implements ElasticParser {
  parseValue(raDecRadiusString: string, elasticSearchProperty: ElasticSearchProperty): any {
    raDecRadiusString = raDecRadiusString?.trim();

    const isRaDecRadiusRegExp = raDecRadiusRegExp.test(raDecRadiusString);
    const isRaDecIntervalRegExp = raDecIntervalRegExp.test(raDecRadiusString);

    if (isRaDecRadiusRegExp) {
      return this.parseRaDecRadiusRegExp(raDecRadiusString, elasticSearchProperty);
    } else if (isRaDecIntervalRegExp) {
      return this.parseRaDecIntervalRegExp(raDecRadiusString, elasticSearchProperty);
    } else {
      return undefined;
    }
  }

  private parseRaDecRadiusRegExp(raDecRadiusString: string, elasticSearchProperty: ElasticSearchProperty): any {
    const match = raDecRadiusRegExp.exec(raDecRadiusString);
    const indexRaInMatches = 1;
    const indexDecInMatches = 9;
    const indexRadiusInMatches = 21;

    const raString = match[indexRaInMatches];
    const decString = match[indexDecInMatches];
    const radiusString = match[indexRadiusInMatches];

    let raNumeric;
    let decNumeric;

    const isRaADegExpr = new RegExp(`^${raDegExp}$`).test(raString);
    const isDecADegExpr = new RegExp(`^${decDegExp}$`).test(decString);

    if (isRaADegExpr) {
      raNumeric = +raString;
    } else {
      const raToDegFormatter = new RaToDegFormatter();
      const raHexString = raString.replace(/(h|m|d)/gi, ':');
      raNumeric = raToDegFormatter.apply(raHexString);
    }

    if (isDecADegExpr) {
      decNumeric = +decString;
    } else {
      const decToDegFormatter = new DecToDegFormatter();
      const decHexString = decString.replace(/(h|m|d)/gi, ':');
      decNumeric = decToDegFormatter.apply(decHexString);
    }

    const radiusNumeric = +radiusString?.replace(',', '') || defaultRaDecRadius;
    const footprintProperty = elasticSearchProperty.property?.footprint;
    return buildQueryAstroCircle(footprintProperty, raNumeric, decNumeric, radiusNumeric);
  }

  private parseRaDecIntervalRegExp(raDecRadiusString: string, elasticSearchProperty: ElasticSearchProperty): any {
    const match = raDecIntervalRegExp.exec(raDecRadiusString);
    const indexRaInMatches = 1;
    const indexDecInMatches = 14;

    const raString = match[indexRaInMatches];
    const decString = match[indexDecInMatches];

    const raNumberParser = new NumberParser();
    const raProperty = elasticSearchProperty.property.ra;
    const raStringParsed = raNumberParser.parseValue(raString, {property: raProperty});

    const decNumberParser = new NumberParser();
    const decProperty = elasticSearchProperty.property.dec;
    const decStringParsed = decNumberParser.parseValue(decString, {property: decProperty});

    return {bool: {must: [raStringParsed, decStringParsed]}};
  }
}

class RaToDegFormatter {
  private readonly decimals = 3;

  apply(ra: string): number {
    const tokens = ra.split(':');
    let hours = +tokens[0];
    let minutes = +tokens[1];
    let seconds = +tokens[2];

    seconds = Math.floor(seconds * Math.pow(10, this.decimals) + 0.5) / Math.pow(10, this.decimals);

    while (seconds >= 60.0) {
      seconds = seconds - 60.0;
      minutes++;
    }

    while (minutes >= 60) {
      minutes = minutes - 60;
      hours++;
    }

    hours = hours % 24;

    return +((Math.abs(hours) + Math.abs(minutes) / 60.0 + Math.abs(seconds) / 3600.0) * 15.0) % 360.0;
  }
}

class DecToDegFormatter {
  private readonly decimals = 3;

  apply(dec: string): number {
    if (!/\\+|-/.test(dec[0])) {
      dec = '+' + dec;
    }

    const sign = dec[0];
    const tokens = dec.substring(1).split(':');
    let angle = +tokens[0];
    let minutes = +tokens[1];
    let seconds = +tokens[2];

    seconds = Math.floor(seconds * Math.pow(10, this.decimals) + 0.5) / Math.pow(10, this.decimals);

    while (seconds >= 60.0) {
      minutes++;
      seconds -= 60.0;
    }

    while (minutes >= 60) {
      angle++;
      minutes -= 60;
    }

    return parseFloat(sign + (Math.abs(angle) + Math.abs(minutes) / 60.0 + Math.abs(seconds) / 3600.0) % 360.0);
  }
}
